/**
 * FacetWP filter accordion (team overview, overview-thinking, etc.).
 */

import { slideDown, slideUp } from '~/js/helpers';

const SLIDE_DURATION = 300;
const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let globalEscapeBound = false;

function updateFilterAccordionSelectionCounts(section) {
	const oneLabel = strl_vars?.team_filter_selected_one;
	const manyTpl = strl_vars?.team_filter_selected_many;

	section.querySelectorAll('[data-filter-panel]').forEach((panel) => {
		const slug = panel.dataset.filterPanel;
		if (!slug) return;
		const trigger = section.querySelector(`[data-filter-category="${slug}"]`);
		if (!trigger) return;
		const countSuffix = trigger.querySelector('[data-filter-count-suffix]');
		const desc = trigger.querySelector('[data-filter-selection-desc]');

		const count = panel.querySelectorAll('.facetwp-checkbox.checked').length;
		if (countSuffix) {
			countSuffix.textContent = count > 0 ? ` (${count})` : '';
		}

		if (desc) {
			if (count > 0) {
				desc.textContent = count === 1 ? oneLabel : manyTpl.replace('%d', String(count));
				desc.hidden = false;
			} else {
				desc.textContent = '';
				desc.hidden = true;
			}
		}
	});
}

function bindGlobalEscape() {
	if (globalEscapeBound) {
		return;
	}
	globalEscapeBound = true;
	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Escape') return;
		for (const section of document.querySelectorAll('[data-filter-accordion]')) {
			const openPanelEl = section.querySelector('[data-filter-panel]:not([hidden])');
			if (!openPanelEl) continue;
			const api = section.strlFilterAccordionApi;
			if (!api?.closeAll) continue;
			const slug = openPanelEl.dataset.filterPanel;
			const triggerForPanel = slug ? section.querySelector(`[data-filter-category="${slug}"]`) : null;
			e.preventDefault();
			api.closeAll(() => triggerForPanel?.focus());
			break;
		}
	});
}

export function initFilterAccordion(container = document) {
	bindGlobalEscape();

	container.querySelectorAll('[data-filter-accordion]').forEach((section) => {
		if (section.dataset.strlFilterAccordionInit) {
			return;
		}
		section.dataset.strlFilterAccordionInit = '1';

		const triggers = section.querySelectorAll('[data-filter-category]');
		const panels = section.querySelectorAll('[data-filter-panel]');

		if (!triggers.length || !panels.length) {
			return;
		}

		function setTriggerState(trigger, expanded) {
			trigger.setAttribute('aria-expanded', String(expanded));
			trigger.querySelector('.team-filter-icon-plus')?.classList.toggle('hidden', expanded);
			trigger.querySelector('.team-filter-icon-minus')?.classList.toggle('hidden', !expanded);
		}

		function closePanel(panel, callback) {
			if (prefersReduced || window.getComputedStyle(panel).display === 'none') {
				panel.classList.add('hidden');
				panel.hidden = true;
				panel.style.removeProperty('display');
				if (callback) callback();
				return;
			}

			slideUp(panel, SLIDE_DURATION);
			setTimeout(() => {
				panel.classList.add('hidden');
				panel.hidden = true;
				panel.style.removeProperty('display');
				if (callback) callback();
			}, SLIDE_DURATION);
		}

		function closeAll(callback) {
			triggers.forEach((t) => setTriggerState(t, false));

			let pending = 0;
			let hasDone = false;

			const done = () => {
				if (hasDone) return;
				hasDone = true;
				if (callback) callback();
			};

			panels.forEach((panel) => {
				if (window.getComputedStyle(panel).display === 'none') {
					panel.classList.add('hidden');
					panel.hidden = true;
					return;
				}
				pending++;
				closePanel(panel, () => {
					pending--;
					if (pending <= 0) done();
				});
			});

			if (pending === 0) done();
		}

		function openPanel(slug) {
			closeAll(() => {
				const panel = section.querySelector(`[data-filter-panel="${slug}"]`);
				const trigger = section.querySelector(`[data-filter-category="${slug}"]`);
				if (!panel || !trigger) return;

				setTriggerState(trigger, true);
				panel.classList.remove('hidden');
				panel.hidden = false;

				if (!prefersReduced) {
					panel.style.removeProperty('display');
					slideDown(panel, SLIDE_DURATION);
				}
			});
		}

		triggers.forEach((trigger) => {
			trigger.addEventListener('click', () => {
				const slug = trigger.dataset.filterCategory;
				if (!slug) return;

				const isOpen = trigger.getAttribute('aria-expanded') === 'true';
				if (isOpen) {
					closeAll();
				} else {
					openPanel(slug);
				}
			});
		});

		section.querySelectorAll('[data-filter-reset]').forEach((btn) => {
			btn.addEventListener('click', () => {
				const panel = btn.closest('[data-filter-panel]');
				const slug = panel?.dataset.filterPanel;
				const triggerForPanel = slug ? section.querySelector(`[data-filter-category="${slug}"]`) : null;
				if (typeof FWP !== 'undefined') FWP.reset();
				closeAll(() => triggerForPanel?.focus());
			});
		});

		section.strlFilterAccordionApi = {
			closeAll,
			updateSelectionCounts: () => updateFilterAccordionSelectionCounts(section),
		};

		updateFilterAccordionSelectionCounts(section);
	});
}
